import { Component, ElementRef, Injector } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CrudEditorParentService,
  CrudEditorSelfService,
  mobilePhoneNumberValidatorWithInternationalSupport,
  postCodeValidator,
  requiredValidator,
  UserStorage,
  Warning
} from '@scisys-uim/uim-common';
import { BusinessUnit, Ranger, RANGER_STATUS_ACTIVE, COST_CENTRE_TYPE_WILDLIFE, RangerSourceMap, BUSINESS_UNIT_TYPE, BUSINESS_UNIT_EMAIL_CONTACT_TYPE } from 'assets-model';
import { BusinessUnitEmailContactService, BusinessUnitService, CostCentreService, RangerService, RangerSourceMapService, RefCodeStorage } from 'assets-service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RangerItemComponentBase } from './ranger-item.component.base';
import { emailOrRangerSourceValidator } from '../../ranger_source/ranger-source-map.validator';
import { filterValidationWarnings, validateRangerAgainstSource } from 'assets-model';
import moment from 'moment';


@Component({
  selector: 'app-ranger-item',
  templateUrl: './ranger-item.component.html',
  styleUrls: ['./ranger-item.component.scss'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class RangerItemComponent extends RangerItemComponentBase {

  constructor(protected rangerService: RangerService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected policeContactService: BusinessUnitEmailContactService,
    protected rangerSourceService: RangerSourceMapService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(rangerService, businessUnitService, costCentreService, policeContactService, rangerSourceService, refCodeStorage, fb, elementRef, router, route, userStorage, dialog, injector, snackbar);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  itemSet() {
    super.itemSet();
    this.policeContactLoadSelectionList();
  }

    createForm(): FormGroup {
      const form = super.createForm();
      form.get('rangerSource')?.setValidators([requiredValidator, emailOrRangerSourceValidator]);
      form.get('postcode')?.setValidators([requiredValidator, postCodeValidator()]);
      form.get('mobilePhone')?.setValidators([mobilePhoneNumberValidatorWithInternationalSupport]);
      form.get('email')?.clearValidators();
      form.get('status')?.clearValidators();
      return form;
    }
  // ...existing code...

  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
    this.businessUnitLoadSelectionList();
    this.costCentreLoadSelectionList();
    this.statusLoadSelectionList();
    this.policeContactLoadSelectionList();
    this.addAllWarnings();
  }

  sourceValidationErrors(errorKeys?: string | string[]): string[] {
    // cache rangerValidationWarnings if performance is an issue, and set cached variable to null on changing anything that could affect the calculated warnings - e.g. in businessUnitChanged
    const rangerValidationWarnings = validateRangerAgainstSource(this.item) || {};
    const filteredResults = filterValidationWarnings(rangerValidationWarnings, errorKeys);
    const messages: string[] = Object.values(filteredResults);
    return messages;
  }

  addAllWarnings(): void {
    super.addAllWarnings();
    this.addWarning("Business Unit, Cost Centre and Address fields are disabled. This Ranger has Firearm Assets, and editing these would be incompatible with current Firearm Asset locations, and Police record of Ranger address.  Return these firearm assets to storage before changing these fields.", () => this.item?.currentFirearmCount > 0);
    this.addWarning("No Police Notification contact has been selected. You will not be able to fully assign firearms until one is selected.", () => this.item?.policeContact == null);
    this.addWarning("Ranger has recently changed address but the police have not yet been notified.", () => this.shouldPromptPoliceAddressChangeNotification());
  }

  getActiveWarnings(): Warning[] {
    var warnings = [...super.getActiveWarnings()];

    for (var message of this.sourceValidationErrors()) {
      warnings.push({ message: message, active() { return true; } });
    }

    return warnings;
  }

  shouldPromptPoliceAddressChangeNotification(): boolean {
    if (this.createMode || this.item?.currentFirearmCount == null || this.item.currentFirearmCount <= 0 || this.item.addressChangedDate == null) {
      return false;
    }

    if (this.item.addressChangeNotifiedOn == null) {
      return true;
    }

    return moment(this.item.addressChangedDate).isAfter(moment(this.item.addressChangeNotifiedOn));
  }

  notifyPoliceOfRangerAddressChange(): void {
    if (this.item?.rangerId == null || this.unsavedChanges()) {
      return;
    }

    this.rangerService.notifyPoliceOfRangerAddressChange(this.item.rangerId).subscribe(ranger => {
      this.item = ranger;
      this.itemSet();
      this.snackbar.open('Police address change notification requested', 'OK', { duration: 3000 });
    });
  }

  setDefaultDisabledFields() {
    super.setDefaultDisabledFields();
    if (!this.form) {
      return;
    }
    this.rangerId?.disable();
    
    if (!this.createMode) {
      this.status?.disable();
    }

    if (this.readOnly) {
      this.volunteerRanger?.disable();
      this.provisionalRanger?.disable();
    } else {
      if (this.item?.currentFirearmCount > 0) {
        this.businessUnit?.disable();
        this.costCentre?.disable();
        if (this.item?.currentFirearmCount > this.item?.currentPersonalFirearmCount) {
          this.volunteerRanger?.disable();
        }
        //TODO: temporarily allow editing of address fields when firearms are assigned, will later remove when doing police notifications
        // this.addr1?.disable();
        // this.addr2?.disable();
        // this.addr3?.disable();
        // this.town?.disable();
        // this.county?.disable();
        // this.postcode?.disable();
      } else {
        this.businessUnit?.enable();
        if (this.item?.businessUnit != null) {
          this.costCentre?.enable();
        }
        this.volunteerRanger?.enable();
        this.addr1?.enable();
        this.addr2?.enable();
        this.addr3?.enable();
        this.town?.enable();
        this.county?.enable();
        this.postcode?.enable();
      }
      
      if (this.item?.provisionalRanger) {
        this.volunteerRanger?.disable();
      } else {
        this.volunteerRanger?.enable();
      }
    }
  }

  isCopyFromSourceEnabled(): boolean {
    return this.item?.currentFirearmCount == 0 && this.item != null && this.sourceValidationErrors(['rangerSourceNameMismatch', 'rangerBusinessUnitMismatch', 'rangerCostCentreMismatch']).length > 0;
  }

  copyFromSource() {
    if (this.item == null) {
      return;
    }
    this.item.name = this.item.rangerSource?.sourceName;
    if (this.item.rangerSource?.businessUnits?.length == 1) {
      this.item.businessUnit = this.item.rangerSource?.businessUnits[0];
    }
    var source_cc_list = this.item.rangerSource?.costCentres?.filter(cc => cc.businessUnit.buId == this.item.businessUnit.buId);
    if (source_cc_list.length == 1) {
      this.item.costCentre = source_cc_list[0];
    }
  }

  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll(true).subscribe(
      data => {
        this.businessUnitSelectionList = BUSINESS_UNIT_TYPE.entityDecorator.ensureContained(this.item && this.item.businessUnit, data.items);
      }
    );
  }

  businessUnitChangedByUser(value: BusinessUnit, unsetDependents?: boolean): void {
    super.businessUnitChangedByUser(value, unsetDependents);
    this.policeContactLoadSelectionList();
  }

  costCentreLoadSelectionList() {
    if (this.item.businessUnit != null) {
      const standardBUServiceCall = this.costCentreService.findByBusinessUnitForType(this.item.businessUnit?.buId, COST_CENTRE_TYPE_WILDLIFE);
      const nationalBUServiceCall = this.costCentreService.findAllByBusinessUnit(this.item.businessUnit?.buId);
      const serviceCall = this.item.businessUnit?.national ? nationalBUServiceCall : standardBUServiceCall;
      serviceCall.subscribe(
        data => {
          this.costCentreSelectionList = data.items;
          this.setDefaultDisabledFields();
        }
      );
    } else {
      this.costCentreSelectionList = [];
      this.setDefaultDisabledFields();
    }
  }

  policeContactLoadSelectionList() {
    if (this.item.businessUnit == null) {
      this.policeContactSelectionList = [];
    } else {
      this.policeContactService.findPoliceContactsByBuId(this.item.businessUnit?.buId).subscribe(
        data => {
          this.policeContactSelectionList = BUSINESS_UNIT_EMAIL_CONTACT_TYPE.entityDecorator.ensureContained(this.item && this.item.policeContact, data.items);
        }
      );
    }
  }

  rangerSourceSelectionListFilterLoad(text: string): Observable<RangerSourceMap[]> {
    return this.rangerSourceService.findByFullFiltersExtended({ rangerExists: false, sourceText: text, sourceValidRangerEmail: true }, null, true, true).pipe(map(result => result.items));
  }

  rangerSourceDisplay(item: RangerSourceMap | string) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return item.email;
  }

  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    super.businessUnitChanged(value, unsetDependents);
    if (value == null || value?.buId != this.item.costCentre?.businessUnit?.buId) {
      this.item.costCentre = null;
    }
    this.costCentreLoadSelectionList();
  }

  provisionalRangerChanged(value: boolean, unsetDependents: boolean = true) {
    if (this.item?.provisionalRanger) {
      this.volunteerRanger?.disable();
    }else{
      this.volunteerRanger?.enable();
    }
  }

  volunteerRangerChanged(value: boolean, unsetDependents: boolean = true) {
    if (this.item?.volunteerRanger) {
      this.provisionalRanger?.disable();
    }else { 
      this.provisionalRanger?.enable();
    }
  }

  isWildlifeManagerRangerAvailable() {
    return this.userStorage.currentUserHasAnyAuthority("ACQUIRE_FIREARM");
  }
}